import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBuildPageHeader, MiButton, MiIcon } from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { mdiClose, mdiHomeOutline, mdiHomePlus } from "@mdi/js";
import {
  AddHomeForm,
  Footer,
  HomeAndAccountSearch,
  InspectionCreation,
  InspectionTemplateSelection,
  useAddHomeForm,
} from "@mibuildweb/components";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

const PAGES = {
  HOME_SEARCH: "HOME_SEARCH",
  CREATE_HOME: "CREATE_HOME",
  INSPECTION_TEMPLATE_SELECTION: "INSPECTION_TEMPLATE_SELECTION",
  CREATE_ASSIGNMENT: "CREATE_ASSIGNMENT",
};

export function CreateNewInspection() {
  const [activeScreen, setActiveScreen] = useState(PAGES.HOME_SEARCH);
  const [selectedHome, setSelectedHome] = useState({});
  const [selectedInspectionTemplate, setSelectedInspectionTemplate] =
    useState();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { classes } = useStyles({ isSmallScreen });
  const { navigate } = useRouter();

  const onNewHomeCreation = (home) => {
    setSelectedHome(home);
    setActiveScreen(PAGES.INSPECTION_TEMPLATE_SELECTION);
  };

  const homeForm = useAddHomeForm({ onCreate: onNewHomeCreation });

  const goBack = () => {
    setActiveScreen(PAGES.HOME_SEARCH);
    setSelectedInspectionTemplate({});
  };

  const handleClose = () => {
    goBack();
    navigate(-1);
  };

  const handleInspectionTemplateSelection = (inspectionTemplate) => {
    setSelectedInspectionTemplate(inspectionTemplate);
    setActiveScreen(PAGES.CREATE_ASSIGNMENT);
  };

  return (
    <Box className={classes.root}>
      <MiBuildPageHeader
        title={`Create New ${ENTITY_NAMES.INSPECTION}`}
        titleIcon={
          <MiIcon path={mdiHomeOutline} color={theme.palette.primary.main} />
        }
        actionComponent={
          <MiButton
            startIcon={{
              path: mdiClose,
            }}
            onClick={handleClose}
            style={{ padding: theme.spacing(1, 1) }}
          />
        }
      />
      {activeScreen === PAGES.HOME_SEARCH && (
        <Box className={classes.searchBox}>
          <HomeAndAccountSearch
            onCreate={() => setActiveScreen(PAGES.CREATE_HOME)}
            onSelect={(option) => {
              setSelectedHome(option);
              setActiveScreen(PAGES.INSPECTION_TEMPLATE_SELECTION);
            }}
            showLogo
          />
        </Box>
      )}
      {activeScreen === PAGES.CREATE_HOME && (
        <Box className={classes.createHomeContainer}>
          <Box className={classes.createHomeBox}>
            <AddHomeForm
              {...homeForm}
              hideFooter
              onCancel={goBack}
              onCreate={homeForm.handleCreateHome}
            />
            <Box className={classes.propertyCreationBox}>
              <Box
                className={[classes.centerBox, classes.centerPropertyCreation]}
              >
                <Box
                  className={classes.centerBox}
                  width={60}
                  height={60}
                  borderRadius={20}
                  backgroundColor={theme.palette.common.white}
                >
                  <MiIcon path={mdiHomePlus} color={theme.palette.green.dark} />
                </Box>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.BODY1}
                  fontWeight={theme.typography.fontWeightBold}
                >
                  Property Creation
                </Typography>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.CAPTION}
                  color={theme.palette.grey.main}
                >
                  To only save the property address, please click the “Save
                  Button” below.
                </Typography>
                <MiButton
                  variant={BUTTON_VARIANTS.CONTAINED}
                  title="Save Property"
                  disabled
                />
              </Box>
            </Box>
          </Box>
          <Footer
            sticky
            show
            saveTitle="Save Property and Create Inspection"
            handleSaveClick={homeForm.handleCreateHome}
            handleCancelClick={() => {
              homeForm.onResetForm();
              goBack();
            }}
            disableSave={homeForm.isSubmitDisabled}
          />
        </Box>
      )}

      {(activeScreen === PAGES.INSPECTION_TEMPLATE_SELECTION ||
        activeScreen === PAGES.CREATE_ASSIGNMENT) && (
        <Box className={classes.inspectionCreationContainer}>
          <Box className={classes.inspectionCreationBox}>
            <InspectionTemplateSelection
              home={selectedHome}
              selectedInspectionTemplate={selectedInspectionTemplate}
              handleInspectionTemplateSelection={
                handleInspectionTemplateSelection
              }
              goBack={goBack}
            />
          </Box>
          <Box className={classes.inspectionCreationBox}>
            <InspectionCreation
              home={selectedHome}
              selectedInspectionTemplate={selectedInspectionTemplate}
              onCancel={() => {
                setActiveScreen(PAGES.INSPECTION_TEMPLATE_SELECTION);
                setSelectedInspectionTemplate({});
              }}
              onSubmit={handleClose}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme, { isSmallScreen }) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.common.white,
    flex: 1,
    width: "100%",
  },
  searchBox: {
    margin: theme.spacing(40, isSmallScreen ? 20 : 60),
  },
  propertyCreationBox: {
    flex: 1,
    backgroundColor: theme.palette.green.light,
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    maxHeight: "100%",
  },
  createHomeContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: 1,
  },
  createHomeBox: {
    display: "flex",
    flexGrow: 1,
    gap: theme.spacing(4),
    margin: theme.spacing(0, 2),
  },
  inspectionCreationContainer: {
    margin: theme.spacing(4, 2, 0),
    display: "flex",
    overflow: "auto",
    height: "100%",
    gap: theme.spacing(2),
  },
  inspectionCreationBox: {
    flex: 1,
    overflow: "auto",
  },
  centerBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  centerPropertyCreation: {
    position: "fixed",
    top: "50%",
    gap: theme.spacing(2),
    transform: "translate(0, -25%)",
  },
}));
